/**
 * Builds the Ads workspace state snapshot sent to the companion on every
 * chat turn. Single source of truth for Marcus via `useManagedAgentChat`.
 *
 * The backend shape is `AdsContextSnapshot` in
 * `core/src/domains/companion/services/adsContext.ts`. This function is
 * typed loosely (`Record<string, unknown>`) because the frontend stores
 * use their own internal types — the backend's `formatAdsContext` tolerates
 * missing fields via optional chaining, so passthrough is safe.
 *
 * Returns `null` when the user is NOT in an ads co-agent session. Callers
 * should omit the `adsContext` field from the request body in that case.
 */

import { useDepartmentStore } from "@/features/department";
import { useAdsWorkspaceStore } from "@/features/ads";
import {
  useAdsResearchStore,
  type AdsDistilledResearch,
  type AdsGeneratedCopy,
  type UgcBrief,
} from "@/features/ads";
import {
  brandSnapshotToBrief,
  buildBrandContextSnapshot,
} from "@/features/brand/lib/buildBrandContextSnapshot";

type AdsDistilledResearchLike = Partial<AdsDistilledResearch>;
type AdsGeneratedCopyLike = Partial<AdsGeneratedCopy>;
type UgcBriefLike = Partial<UgcBrief>;

export interface BuildAdsContextSnapshotOpts {
  /**
   * Per-type cap on rendered generatedAssets. Default 20 each. Raise for
   * agents that need broader creative inventory (e.g. Agent Mads picks ads
   * to publish, so a 10-asset cap can hide perfectly-good videos behind a
   * batch of recent images).
   *
   * NOTE: `imageAssetLimit` applies to *creative* images only. UGC scaffolding
   * (avatar portraits + element refs) has its own `ugcScaffoldingLimit` so the
   * pre-video reference set isn't crowded out by a recent batch of static ads.
   */
  imageAssetLimit?: number;
  videoAssetLimit?: number;
  /**
   * Cap on UGC scaffolding assets (assetKind ∈ {ugc-portrait, ugc-reference}).
   * Default 60 — comfortably covers 3 videos × ~5 element refs × multiple
   * script angles + portraits + text-frame stills, which is what Marcus needs
   * to verify all videos are prepped before dispatching video generation.
   */
  ugcScaffoldingLimit?: number;
}

function getAdsPageContext(): Record<string, string> | undefined {
  if (typeof window === "undefined") return undefined;

  const pathname = window.location.pathname;
  if (pathname.endsWith("/google-campaigns")) {
    return {
      path: pathname,
      surface: "google_campaigns",
      label: "Google campaigns",
    };
  }
  if (pathname.endsWith("/paid-campaigns")) {
    return {
      path: pathname,
      surface: "paid_campaigns",
      label: "Paid campaigns",
    };
  }
  if (pathname.endsWith("/ads")) {
    return {
      path: pathname,
      surface: "ads_workspace",
      label: "Ads workspace",
    };
  }

  return { path: pathname, surface: "unknown", label: "Ads workspace" };
}

export function buildAdsContextSnapshot(
  opts?: BuildAdsContextSnapshotOpts,
): Record<string, unknown> | null {
  const dept = useDepartmentStore.getState();
  if (dept.coAgentType !== "ads") return null;

  const ads = useAdsWorkspaceStore.getState();
  const research = useAdsResearchStore.getState();
  const currentPage = getAdsPageContext();

  // Brand DNA summary — only included when the user has selected (or
  // auto-inherited) a brand AND its full BrandConfig has been fetched.
  // Shape mirrors `BrandBrief` in
  // core/src/domains/companion/services/brandContext.ts. Derived via the
  // shared `buildBrandContextSnapshot` so ads + social ship the same brand
  // context (Phase 5 follow-up — single source of truth).
  const brandSummary = brandSnapshotToBrief(buildBrandContextSnapshot(ads.selectedBrandConfig));

  // Research snapshot — Marcus's per-turn context carries a **digest** per brief
  // (not the full distilledResearch/adCopy/ugcBrief) plus the research-brief
  // artifact id. Marcus pulls the full record on demand via get_research_brief
  // or passes { researchBriefId, angleIndices } straight into
  // ads_generate_images_from_research. See AdsContextSnapshot.research in
  // core/src/domains/companion/services/adsContext.ts for the wire shape.
  const buildDigest = (r: {
    distilledResearch?: AdsDistilledResearchLike;
    adCopy?: AdsGeneratedCopyLike;
    ugcBrief?: UgcBriefLike;
  }): Record<string, unknown> => {
    const digest: Record<string, unknown> = {};
    const problemCount = r.distilledResearch?.userProblems?.length;
    if (typeof problemCount === "number") digest.problemCount = problemCount;
    const angleCount = r.adCopy?.imageOverlayCopy?.length;
    if (typeof angleCount === "number") digest.angleCount = angleCount;
    const personaCount = r.ugcBrief?.avatars?.length;
    if (typeof personaCount === "number") digest.personaCount = personaCount;
    const audience = r.distilledResearch?.audienceInsights?.demographics;
    if (audience) digest.audience = audience;
    const topHooks = r.ugcBrief?.hooks
      ?.slice(0, 3)
      .map((h) => ({ type: h.type, text: h.text }))
      .filter((h) => h.text);
    if (topHooks && topHooks.length > 0) digest.topHooks = topHooks;
    const topSellingPoints = r.adCopy?.imageOverlayCopy
      ?.slice(0, 5)
      .map((c) => c.text)
      .filter((t): t is string => typeof t === "string" && t.length > 0);
    if (topSellingPoints && topSellingPoints.length > 0) {
      digest.topSellingPoints = topSellingPoints;
    }
    return digest;
  };

  let researchBlock: Record<string, unknown> | undefined;
  if (research.status === "running") {
    researchBlock = {
      status: research.status,
      phase: research.phase,
      ...(research.error ? { error: research.error } : {}),
    };
  } else if (research.status === "error") {
    researchBlock = {
      status: research.status,
      ...(research.error ? { error: research.error } : {}),
    };
  }

  // Dedupe saved results by productId, newest-first. Split into "active brief"
  // (matches activeProductId when set, else the newest saved entry) and the
  // rest (surfaced as savedResults so Marcus can switch products without a
  // tool call).
  const seen = new Set<string>();
  const deduped: typeof research.savedResults = [];
  for (const r of research.savedResults) {
    const key = r.productId ?? r.artifactId ?? "";
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    deduped.push(r);
  }

  // Prefer LIVE state from the just-finished pipeline when we have it. The
  // `useArtifacts` hook that backs `savedResults` only refetches on mount /
  // filter change, so right after the pipeline emits `done` the fresh brief
  // is NOT yet in `savedResults` — if we only built the digest from
  // `savedResults`, Marcus's very next turn would see no researchBriefId and
  // have to guess, which is exactly what caused the 404s from
  // get_research_brief.
  const liveProductId = research.activeProductId ?? research.selectedProductId ?? null;
  const liveProduct = liveProductId ? ads.products.find((p) => p.id === liveProductId) : undefined;
  const hasLiveBrief =
    research.researchBriefId &&
    research.distilledResearch &&
    research.adCopy &&
    (research.status === "complete" || research.status === "idle");

  if (hasLiveBrief) {
    const others = deduped
      .filter((r) => {
        if (r.artifactId === research.researchBriefId) return false;
        if (liveProductId && r.productId === liveProductId) return false;
        return true;
      })
      .slice(0, 3);

    researchBlock = {
      ...(researchBlock ?? {}),
      ...(researchBlock?.status ? {} : { status: "saved" }),
      researchBriefArtifactId: research.researchBriefId!,
      ...(liveProductId ? { productId: liveProductId } : {}),
      ...(liveProduct?.title ? { productTitle: liveProduct.title } : {}),
      digest: buildDigest({
        distilledResearch: research.distilledResearch ?? undefined,
        adCopy: research.adCopy ?? undefined,
        ugcBrief: research.ugcBrief ?? undefined,
      }),
      ...(others.length > 0
        ? {
            savedResults: others.map((r) => ({
              artifactId: r.artifactId,
              ...(r.productId ? { productId: r.productId } : {}),
              ...(r.productTitle ? { productTitle: r.productTitle } : {}),
              digest: buildDigest(r),
            })),
          }
        : {}),
    };
  } else if (deduped.length > 0) {
    const activeProductId = research.activeProductId ?? research.selectedProductId;
    const activeIdx = activeProductId
      ? deduped.findIndex((r) => r.productId === activeProductId)
      : 0;
    const active = activeIdx >= 0 ? deduped[activeIdx] : deduped[0];
    const others = deduped.filter((_, i) => i !== (activeIdx >= 0 ? activeIdx : 0)).slice(0, 3);

    researchBlock = {
      ...(researchBlock ?? {}),
      ...(researchBlock?.status ? {} : { status: "saved" }),
      researchBriefArtifactId: active.artifactId,
      ...(active.productId ? { productId: active.productId } : {}),
      ...(active.productTitle ? { productTitle: active.productTitle } : {}),
      digest: buildDigest(active),
      ...(others.length > 0
        ? {
            savedResults: others.map((r) => ({
              artifactId: r.artifactId,
              ...(r.productId ? { productId: r.productId } : {}),
              ...(r.productTitle ? { productTitle: r.productTitle } : {}),
              digest: buildDigest(r),
            })),
          }
        : {}),
    };
  }

  // Split caps so a flood of recent images doesn't starve videos (or vice
  // versa). `ads.generatedAds` is newest-first (see store.mergeAds).
  //
  // UGC scaffolding (avatar portraits + element refs) gets its own bucket so
  // a batch of refs across 3 videos (~15+ images) doesn't get truncated by the
  // creative-image cap. Without this, Marcus loses visibility on the later
  // videos' refs when dispatching video generation — he can't confirm all
  // moodboard sets are ready.
  const imageLimit = opts?.imageAssetLimit ?? 10;
  const videoLimit = opts?.videoAssetLimit ?? 10;
  const ugcScaffoldingLimit = opts?.ugcScaffoldingLimit ?? 60;
  const capped: typeof ads.generatedAds = [];
  let imgCount = 0;
  let vidCount = 0;
  let ugcCount = 0;
  for (const ad of ads.generatedAds) {
    if (ad.type === "image") {
      const meta = (ad as { metadata?: Record<string, unknown> }).metadata;
      const kind = meta && typeof meta.assetKind === "string" ? meta.assetKind : undefined;
      const isUgcScaffolding = kind === "ugc-portrait" || kind === "ugc-reference";
      if (isUgcScaffolding) {
        if (ugcCount >= ugcScaffoldingLimit) continue;
        ugcCount += 1;
      } else {
        if (imgCount >= imageLimit) continue;
        imgCount += 1;
      }
    } else if (ad.type === "video") {
      if (vidCount >= videoLimit) continue;
      vidCount += 1;
    } else {
      continue;
    }
    capped.push(ad);
    if (imgCount >= imageLimit && vidCount >= videoLimit && ugcCount >= ugcScaffoldingLimit) {
      break;
    }
  }

  const generatedAssets = capped
    .map((ad) => {
      const meta = (ad as { metadata?: Record<string, unknown> }).metadata;
      // Read lineage from pipeline_metadata with flat-field fallback.
      const pm =
        meta && typeof meta.pipeline_metadata === "object" && meta.pipeline_metadata
          ? (meta.pipeline_metadata as Record<string, unknown>)
          : undefined;
      const sourceResearchAngle =
        (pm && typeof pm.researchAngle === "string" ? pm.researchAngle : undefined) ??
        (meta && typeof meta.sourceResearchAngle === "string"
          ? meta.sourceResearchAngle
          : undefined);
      const sourceVideoScriptAngle =
        meta && typeof meta.sourceVideoScriptAngle === "string"
          ? meta.sourceVideoScriptAngle
          : undefined;
      const sourceImageJobId =
        (pm && typeof pm.parentArtifactId === "string" ? pm.parentArtifactId : undefined) ??
        (meta && typeof meta.sourceImageJobId === "string" ? meta.sourceImageJobId : undefined);
      const rawSceneSlot =
        meta && typeof meta.ugcSceneSlot === "string" ? meta.ugcSceneSlot : undefined;
      const ugcSceneSlot =
        rawSceneSlot === "hook" ||
        rawSceneSlot === "setup" ||
        rawSceneSlot === "reveal" ||
        rawSceneSlot === "payoff"
          ? (rawSceneSlot as "hook" | "setup" | "reveal" | "payoff")
          : undefined;
      const ugcSceneIndex =
        meta && typeof meta.ugcSceneIndex === "number" ? meta.ugcSceneIndex : undefined;
      const ugcVideoIndex =
        meta && typeof meta.ugcVideoIndex === "number" ? meta.ugcVideoIndex : undefined;
      const ugcTextFrameMeta =
        meta && typeof meta.ugcTextFrame === "object" && meta.ugcTextFrame
          ? (meta.ugcTextFrame as Record<string, unknown>)
          : undefined;
      const assignedBeat =
        (meta && typeof meta.assignedBeat === "string" ? meta.assignedBeat : undefined) ??
        (ugcTextFrameMeta && typeof ugcTextFrameMeta.assignedBeat === "string"
          ? ugcTextFrameMeta.assignedBeat
          : undefined);
      const assignedAvatarId =
        meta && typeof meta.assignedAvatarId === "string" ? meta.assignedAvatarId : undefined;
      // UGC lineage signals — projected so Marcus can answer "are portraits +
      // element refs ready?" without re-dispatching. The portrait persona id
      // and element key live either at the top level (Banan production leg)
      // or nested under metadata.experiment.* (GPT Image 2 leg); we read
      // both so either generator surfaces equivalently.
      const assetKind = meta && typeof meta.assetKind === "string" ? meta.assetKind : undefined;
      const experimentMeta =
        meta && typeof meta.experiment === "object" && meta.experiment
          ? (meta.experiment as Record<string, unknown>)
          : undefined;
      const sourceAvatarPersonaId =
        (meta && typeof meta.sourceAvatarPersonaId === "string"
          ? meta.sourceAvatarPersonaId
          : undefined) ??
        (experimentMeta && typeof experimentMeta.sourceAvatarPersonaId === "string"
          ? experimentMeta.sourceAvatarPersonaId
          : undefined);
      const ugcElementMeta =
        meta && typeof meta.ugcElement === "object" && meta.ugcElement
          ? (meta.ugcElement as Record<string, unknown>)
          : experimentMeta &&
              typeof experimentMeta.ugcElement === "object" &&
              experimentMeta.ugcElement
            ? (experimentMeta.ugcElement as Record<string, unknown>)
            : undefined;
      const elementKey =
        ugcElementMeta && typeof ugcElementMeta.elementKey === "string"
          ? ugcElementMeta.elementKey
          : undefined;
      const elementKind =
        ugcElementMeta && typeof ugcElementMeta.kind === "string" ? ugcElementMeta.kind : undefined;
      const generator = meta && typeof meta.generator === "string" ? meta.generator : undefined;
      const resultId = (ad.result as any).id;
      return {
        type: ad.type,
        url: ad.type === "video" ? (ad.result as any).video_output : (ad.result as any).output,
        thumbnailUrl: ad.type === "video" ? (ad.result as any).video_thumbnail : undefined,
        id: resultId,
        selected:
          typeof resultId === "string" && resultId.length > 0
            ? ads.selectedAssetIds.includes(resultId)
            : false,
        favorite:
          typeof resultId === "string" && resultId.length > 0
            ? ads.favoriteAssetIds.includes(resultId)
            : false,
        ...(sourceResearchAngle ? { sourceResearchAngle } : {}),
        ...(sourceVideoScriptAngle ? { sourceVideoScriptAngle } : {}),
        ...(sourceImageJobId ? { sourceImageJobId } : {}),
        ...(ugcSceneSlot ? { ugcSceneSlot } : {}),
        ...(ugcSceneIndex !== undefined ? { ugcSceneIndex } : {}),
        ...(ugcVideoIndex !== undefined ? { ugcVideoIndex } : {}),
        ...(assignedBeat ? { assignedBeat } : {}),
        ...(assignedAvatarId ? { assignedAvatarId } : {}),
        ...(assetKind ? { assetKind } : {}),
        ...(sourceAvatarPersonaId ? { sourceAvatarPersonaId } : {}),
        ...(elementKey ? { elementKey } : {}),
        ...(elementKind ? { elementKind } : {}),
        ...(generator ? { generator } : {}),
      };
    })
    .filter((a) => a.url);

  // Include pending (in-progress) jobs so Marcus knows what is still running.
  // Only jobs whose status is truly in-flight (submitted/running) belong here —
  // completed jobs surface via `generatedAssets`, and failed ones surface via
  // the __jobs_completed__ event. Emitting them here too would double-count
  // and inflate every turn's context.
  const isInFlight = (status: string | undefined): boolean =>
    status === "submitted" || status === "running";
  const pendingJobs: Array<Record<string, unknown>> = [];
  for (const j of ads.pendingBananJobs) {
    if (!isInFlight(j.status)) continue;
    pendingJobs.push({
      agent: "banan",
      type: "image",
      jobId: j.jobId,
      status: j.status,
      ...(j.productName ? { productName: j.productName } : {}),
      ...(j.sourceResearchAngle ? { sourceResearchAngle: j.sourceResearchAngle } : {}),
    });
  }
  for (const j of ads.pendingKlingJobs) {
    if (!isInFlight(j.status)) continue;
    pendingJobs.push({
      agent: "vid",
      type: "video",
      jobId: j.jobId,
      status: j.status,
      // Single-session batches: itemCount is the number of videos being
      // generated in this one job. Absent for legacy single-video jobs.
      ...(j.itemCount ? { itemCount: j.itemCount } : {}),
      ...(j.productName ? { productName: j.productName } : {}),
      ...(j.sourceResearchAngle ? { sourceResearchAngle: j.sourceResearchAngle } : {}),
      ...(j.sourceVideoScriptAngle ? { sourceVideoScriptAngle: j.sourceVideoScriptAngle } : {}),
    });
  }
  // Higgsfield Marketing Studio video jobs come from the VideoAdSetupModal
  // dispatch (not Marcus). Surfacing them here lets Marcus answer "is the
  // video ready?" / acknowledge the user's modal-fire dispatches.
  for (const j of ads.pendingHiggsfieldJobs) {
    if (!isInFlight(j.status)) continue;
    pendingJobs.push({
      agent: "higgsfield",
      type: "video",
      jobId: j.jobId,
      status: j.status,
      ...(j.itemCount ? { itemCount: j.itemCount } : {}),
      ...(j.productName ? { productName: j.productName } : {}),
      ...(j.videoTypeId ? { videoTypeId: j.videoTypeId } : {}),
      ...(j.higgsfieldMode ? { higgsfieldMode: j.higgsfieldMode } : {}),
    });
  }
  // Ad platform connection status for publishing awareness
  const platformConfig = ads.adPlatformConfig;
  const adPlatforms: Record<string, unknown> = {
    google: {
      connected: platformConfig.google.connected,
      ...(platformConfig.google.customerId ? { customerId: platformConfig.google.customerId } : {}),
      ...(platformConfig.google.accountName
        ? { accountName: platformConfig.google.accountName }
        : {}),
    },
    meta: {
      connected: platformConfig.meta.connected,
      ...(platformConfig.meta.accountId ? { accountId: platformConfig.meta.accountId } : {}),
      ...(platformConfig.meta.accountName ? { accountName: platformConfig.meta.accountName } : {}),
      ...(platformConfig.meta.pageId ? { pageId: platformConfig.meta.pageId } : {}),
      ...(platformConfig.meta.pageName ? { pageName: platformConfig.meta.pageName } : {}),
    },
  };

  return {
    ...(currentPage ? { currentPage } : {}),
    products: ads.products,
    selectedProductIds: ads.selectedProductIds,
    activeTab: ads.activeTab,
    generatedAdCount: ads.generatedAds.length,
    selectedAssetIds: ads.selectedAssetIds,
    favoriteAssetIds: ads.favoriteAssetIds,
    generatedAssets,
    adPlatforms,
    ...(platformConfig.meta.accountId
      ? { targetMetaAdAccountId: platformConfig.meta.accountId }
      : {}),
    ...(pendingJobs.length > 0 && { pendingJobs }),
    ...(researchBlock && { research: researchBlock }),
    ...(brandSummary && { brand: brandSummary }),
  };
}
